import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from "@angular/forms";
import { ImageCroppedEvent } from "ngx-image-cropper";
import { ConfirmModalComponent } from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoService } from "@jsverse/transloco";
import imageCompression from 'browser-image-compression';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faTrash,
  faUpload,
} from '@fortawesome/pro-light-svg-icons';
import { SafeUrlPipe } from "./safe-url.pipe";
import { ImageCropperComponent } from 'ngx-image-cropper';
import { TranslocoPipe } from '@jsverse/transloco';

/**
 * Input component that is attached to a {@link FormControl} to pick and select an image and crop it via image cropper.
 */
@Component({
  selector: 'ct-lib-image-editor',
  templateUrl: './image-editor.component.html',
  styleUrls: ['./image-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SafeUrlPipe,
    ConfirmModalComponent,
    ImageCropperComponent,
    TranslocoPipe,
    FaIconComponent,
  ],
})
export class ImageEditorComponent {
  private toastsService = inject(ToastsService);
  private translateService = inject(TranslocoService);

  protected readonly faTrash = faTrash;
  protected readonly faUpload = faUpload;

  readonly control = input(new UntypedFormControl(null, []));
  readonly submitAttempt = input(false);
  readonly ratio = input(1);
  readonly maintainRatio = input(true);
  readonly imageSrc = input<string | null>(null);
  /** Optional fallback image URL/data URL when there is no `imageSrc` / crop preview. */
  readonly placeholder = input<string | undefined>(undefined);
  readonly accept = input('image/png, image/jpeg');
  readonly small = input(false);
  readonly label = input('');
  @Output() onConfirm = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @ViewChild('cropperModal') cropperModal!: ConfirmModalComponent;
  private readonly FILE_SIZE_LIMIT: number = 20000000;
  inputFile: File | null = null;
  croppedImage: string | null = null;
  inputId = `imageInput${Math.ceil(Math.random() * 9999).toString()}`;

  /** Local preview after crop; parent `imageSrc` when unset. */
  private readonly displaySrc = signal<string | null>(null);

  readonly imageForDisplay = computed((): string | null => {
    const a = this.displaySrc();
    if (this.isNonEmptyUrl(a)) return a;
    const b = this.imageSrc();
    if (this.isNonEmptyUrl(b)) return b;
    const c = this.placeholder();
    if (this.isNonEmptyUrl(c)) return c;
    return null;
  });

  private isNonEmptyUrl(value: string | null | undefined): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }

  fileChangeEvent(event: any): void {
    if (event.target.files?.length !== 0) {
      if (this.checkTypeAllowed(event.target.files[0]?.type)) {
        imageCompression(event.target.files[0], {maxWidthOrHeight: 1000}).then(
          compressedImage => {
            if (compressedImage.size < this.FILE_SIZE_LIMIT) {
              this.inputFile = compressedImage;
              this.cropperModal.modal.open();
            } else {
              this.translateService
                .selectTranslate('ct-library.file-input.file-size-exceeded', {
                  fileName: event.target.files[0]?.name,
                })
                .subscribe({
                  next: result => {
                    this.toastsService.alert(result);
                  },
                });
            }
          }
        );
      } else {
        this.translateService
          .selectTranslate('ct-library.image-editor.wrong-type-toast', {
            fileName: event.target.files[0]?.name,
          })
          .subscribe({
            next: result => {
              this.toastsService.alert(result);
            },
          });
      }
    }
  }

  imageCropped(cropped: ImageCroppedEvent) {
    this.croppedImage = cropped.base64 ?? null;
  }

  confirmCrop() {
    const src = this.croppedImage;
    if (src === null) return;
    this.displaySrc.set(src);
    this.onConfirm.emit(src);
    this.urlToFile(src, 'image.png', 'image/png').then(result => {
      if (result.size < this.FILE_SIZE_LIMIT) {
        this.control().patchValue(result);
      } else {
        this.translateService
          .selectTranslate('ct-library.file-input.file-size-exceeded', {
            fileName: result.name,
          })
          .subscribe({
            next: result => {
              this.toastsService.alert(result);
            },
          });
      }
    });
  }

  checkTypeAllowed(type: string) {
    const accept = this.accept();
    const acceptedTypes = accept.split(/, ?/);
    if (acceptedTypes.includes('image/jpeg')) acceptedTypes.push('image/jpg');
    return accept === '*' ? true : acceptedTypes.includes(type);
  }

  onDeleteFn() {
    this.control().patchValue(null);
    this.displaySrc.set(null);
    this.croppedImage = null;
    this.onDelete.emit();
  }

  urlToFile(url: string, filename: string, mimeType: string): Promise<File> {
    return fetch(url)
      .then(res => {
        return res.arrayBuffer();
      })
      .then(buf => {
        return new File([buf], filename, {type: mimeType});
      });
  }
}
